import type { StatusTone } from '../../theme';

/** One event from authMonitoringService (in-memory, this browser tab only). */
export interface RecordedAuthEvent {
  type: string;
  timestamp: number;
  userIdentifier?: string;
  errorCode?: string;
  context?: string;
  metadata?: Record<string, unknown>;
}

/** "password_reset_confirm" -> "Password reset confirm". */
export const humanize = (raw?: string | null): string => {
  const words = String(raw || '')
    .trim()
    .replace(/^auth\//, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/** Label and chip tone for an event, in words instead of the raw type. */
export const getAuthEventMeta = (event: Pick<RecordedAuthEvent, 'type' | 'metadata'>): { label: string; tone: StatusTone } => {
  switch (event.type) {
    case 'login_attempt':
      return { label: 'Sign-in attempt', tone: 'neutral' };
    case 'login_success':
      return { label: 'Signed in', tone: 'done' };
    case 'login_failure':
      return { label: 'Failed attempt', tone: 'problem' };
    case 'rate_limit_triggered':
      return { label: 'Rate limited', tone: 'attention' };
    case 'token_refresh':
      return event.metadata?.success === false
        ? { label: 'Session refresh failed', tone: 'problem' }
        : { label: 'Session refreshed', tone: 'neutral' };
    case 'session_expired':
      return { label: 'Session expired', tone: 'attention' };
    default:
      return { label: humanize(event.type) || 'Event', tone: 'neutral' };
  }
};

const ERROR_LABELS: Record<string, string> = {
  'auth/wrong-password': 'Wrong password',
  'auth/invalid-credential': 'Wrong email or password',
  'auth/invalid-login-credentials': 'Wrong email or password',
  'auth/user-not-found': 'No account for that email',
  'auth/invalid-email': 'Invalid email address',
  'auth/too-many-requests': 'Too many attempts',
  'auth/user-disabled': 'Account disabled',
  'auth/network-request-failed': 'Network error',
  'auth/internal-error': 'Sign-in service error',
  'auth/operation-not-allowed': 'Sign-in method turned off',
  'auth/expired-action-code': 'Reset link expired',
  'auth/invalid-action-code': 'Reset link not valid',
  'auth/weak-password': 'Password too weak',
  'auth/requires-recent-login': 'Needs a recent sign-in',
};

/** A Firebase error code in words ("auth/wrong-password" -> "Wrong password"). */
export const describeAuthError = (code?: string | null): string => {
  if (!code) return '';
  return ERROR_LABELS[code] ?? (humanize(code) || 'Other');
};

const CONTEXT_LABELS: Record<string, string> = {
  user_form: 'Sign-in form',
  login_form: 'Sign-in form',
  login: 'Sign-in form',
  email_password: 'Email and password',
  password_reset: 'Password reset request',
  password_reset_confirm: 'New password',
  automatic: 'Automatic',
  apollo_retry: 'Retried request',
  apollo_retry_failed: 'Retried request',
  force_refresh_failed: 'Forced refresh',
  token_refresh_failed: 'Session refresh failed',
};

/** Where an event came from, in words. */
export const describeContext = (context?: string | null): string => {
  if (!context) return '';
  return CONTEXT_LABELS[context] ?? humanize(context);
};

/** Newest first; the service keeps them oldest first. */
export const newestFirst = (events: RecordedAuthEvent[]): RecordedAuthEvent[] =>
  [...events].sort((a, b) => b.timestamp - a.timestamp);

export interface ReasonRow {
  code: string;
  label: string;
  count: number;
  share: number;
}

/** Failed attempts by reason, largest first, with each one's share. */
export const buildReasonRows = (breakdown: Record<string, number> | undefined, failures: number): ReasonRow[] =>
  Object.entries(breakdown || {})
    .map(([code, count]) => ({
      code,
      label: describeAuthError(code),
      count: Number(count) || 0,
      share: failures > 0 ? Math.round(((Number(count) || 0) / failures) * 100) : 0,
    }))
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);

/** A metadata value for the details dialog. */
export const formatMetadataValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  // Identifiers such as "auth_failure" read as words; sentences pass through.
  return /^[a-z]+(_[a-z]+)+$/.test(text) ? humanize(text) : text;
};
